import { AssetDTO } from '../../service/asset';
import { VisionWindowManager } from '../../window/manager';
import { VisionWindow } from '../../window/window';
import { PreventativeMaintenanceTab } from './pm';
import { PMDTO, PMTypeDTO, PMWork } from './pmservice';

export class PMNotesWindow extends VisionWindow {
    layout!: dhtmlXLayoutObject;
    form!: dhtmlXForm;
    notesGrid!: dhtmlXGridObject;

    tab: PreventativeMaintenanceTab;
    id!: string;
    currentPMId!: string;

    constructor(manager: VisionWindowManager, tab: PreventativeMaintenanceTab) {
        const height = window.innerHeight * 0.75;
        const width = window.innerWidth * 0.45;

        super(manager, {
            id: 'complete_pm',
            title: 'Complete PM',
            x: 30,
            y: 30,
            width: width,
            height: height,
        });
        this.tab = tab;

        this.window.attachEvent('onClose', (win) => {
            this.tab.notesWindow?.window.hide();
            return false;
        });

        this.window.setText('PM Notes');
        this.buildUI();
        this.setupEvents();
    }

    private buildUI(): void {
        this.layout = this.window.attachLayout('2E');
        this.layout.cells('a').hideHeader();
        this.layout.cells('b').hideHeader();

        this.notesGrid = this.layout.cells('b').attachGrid();
        this.notesGrid.setHeader('Date,Notes,Cost,Remove', '', ['text-align:left;', 'text-align:left;', 'text-align:center;', 'text-align:center;']);
        this.notesGrid.setColTypes('dhxCalendarA,txt,price,img');
        this.notesGrid.setColAlign('left,left,center,center');
        this.notesGrid.init();

        const formData = [
            { type: 'calendar', name: 'pm_note_date', label: 'Date:', enableTime: true, dateFormat: '%m-%d-%Y', labelWidth: '80', position: 'label-left', offsetLeft: '12' },
            { type: 'input', name: 'pm_notes', label: 'Notes:', rows: 5, labelWidth: '80', position: 'label-left', offsetLeft: '12', inputWidth: 300 },
            { type: 'input', name: 'pm_cost', label: 'Cost:', labelWidth: '80', position: 'label-left', offsetLeft: '12', inputWidth: 100 },
            { type: 'button', name: 'submit', value: 'Save Note', labelWidth: '80', position: 'label-left', offsetLeft: '12' },
        ];
        this.form = this.layout.cells('a').attachForm(formData);
        this.form.getInput('pm_note_date').autocomplete = 'off';
        this.form.setItemValue('pm_note_date', new Date());

        const btnWrapper = document.createElement('div');
        btnWrapper.className = 'custom-btn-wrapper';
        btnWrapper.id = 'submit_button_notes';
        const submitBtn = document.createElement('button');
        submitBtn.type = 'button';
        submitBtn.className = 'big-green-btn';
        submitBtn.textContent = 'Done';

        submitBtn.addEventListener('click', () => {
            dhtmlx.confirm({
                type: 'confirm',
                title: 'Notes added',
                text: 'Is the PM complete or more work left to do? The PM will stay open until it is complete.',
                ok: 'PM Completed',
                cancel: 'More work.',
                callback: (result: boolean) => {
                    if (result == true) {
                        this.tab.showCompleteWindow(this.id);
                        this.window.close();
                    } else {
                        this.window.close();
                    }
                },
            });
        });

        // Append button to wrapper, then wrapper to form container
        btnWrapper.appendChild(submitBtn);
        document.body.appendChild(btnWrapper);

        this.layout.attachFooter('submit_button_notes');
    }

    /** Load the PM instance, PM type, and asset information into the form and grid.
     * This will populate the form fields with the appropriate information and load
     * any existing notes for the PM instance into the grid.
     *
     * @param pmInstance The PM instance to load data for. If undefined, will just show the form with default values.
     * @param pmType The PM Type of the PM instance. Used to determine which due fields to show and how to calculate the next due values. If undefined, will not show any due fields or calculate next due values.
     * @param asset The asset of the PM instance. Used to calculate the next due values. If undefined, will not calculate next due values.
     * @return void
     */
    public loadPM(pmInstance: PMDTO | undefined, pmType: PMTypeDTO | undefined, asset: AssetDTO | undefined): void {
        this.id = pmInstance?.id ?? '';
        this.notesGrid.clearAll(false);

        this.form.setItemValue('asset', asset?.id.name ?? 'Unknown Asset');
        this.form.setItemValue('pm_name', pmType?.name ?? 'Unknown PM');

        this.window.setText('Notes for: ' + (pmType?.name ?? 'Unknown PM') + ' on ' + (asset?.id.name ?? 'Unknown Asset'));

        this.currentPMId = pmInstance?.id ?? '';
        this.notesGrid.clearAll(false);
        pmInstance?.work.forEach((item) => {
            this.notesGrid.addRow(item.id, [new Date(Number(item.date)), item.description, item.cost, 'icons/clear.png'], 0, '', '', false);
        });
    }

    /** Set up event listeners for the form and grid.
     * This includes handling the save note button click and the delete note click in the grid.
     */
    private setupEvents(): void {
        this.form.attachEvent('onButtonClick', (id) => {
            this.saveNote();
        });

        this.notesGrid.attachEvent('onRowSelect', (rid, cid) => {
            this.noteSelected(rid, cid);
        });
    }

    private noteSelected(rid: string, cid: number) {
        // Clicked on the X
        if (cid == 3) {
            this.tab.service.deleteNote(this.currentPMId, rid);
            this.notesGrid.deleteRow(rid);
            this.tab.service.dirty = true;
        }
    }

    private saveNote(): void {
        const date = this.form.getItemValue('pm_note_date');
        const note = this.form.getItemValue('pm_notes');
        const price = this.form.getItemValue('pm_cost');

        const notesItem = {} as PMWork;
        notesItem.id = crypto.randomUUID();
        notesItem.description = note;
        notesItem.date = date.getTime();
        notesItem.cost = price;

        this.tab.service.saveNote(this.id, notesItem);
        this.notesGrid.addRow(notesItem.id, [new Date(Number(notesItem.date)), note, price, 'icons/clear.png'], 0, '', '', false);

        this.form.setItemValue('pm_note_date', '');
        this.form.setItemValue('pm_notes', '');
        this.form.setItemValue('pm_cost', '');

        this.tab.service.dirty = true;
    }
}
